import { useMemo } from 'react';
import { PermissionLevel } from '../../api';

export interface ExpandableCellItem {
  key: string;
  label: string;
  level?: PermissionLevel | null;
}

interface ExpandableResourceCellProps {
  items: ExpandableCellItem[];
  pluralLabel: string;
  singularLabel: string;
  expanded: boolean;
  onToggle: () => void;
}

export function ExpandableResourceCell({
  items,
  pluralLabel,
  singularLabel,
  expanded,
  onToggle,
}: ExpandableResourceCellProps) {
  const sortedItems = useMemo(
    () => [...items].sort((a, b) => a.label.localeCompare(b.label)),
    [items]
  );

  const caret = expanded ? '▴' : '▾';

  if (sortedItems.length === 0) {
    return (
      <button
        type="button"
        className={`expandable-cell-toggle empty ${expanded ? 'expanded' : ''}`}
        onClick={onToggle}
      >
        <span className="expandable-cell-summary">—</span>
        <span className="expandable-cell-caret">{caret}</span>
      </button>
    );
  }

  const first = sortedItems[0];
  const otherCount = sortedItems.length - 1;
  const tail =
    otherCount === 0
      ? ''
      : otherCount === 1
      ? ` and 1 other ${singularLabel}`
      : ` and ${otherCount} other ${pluralLabel}`;

  return (
    <button
      type="button"
      className={`expandable-cell-toggle ${expanded ? 'expanded' : ''}`}
      onClick={onToggle}
    >
      <span className="expandable-cell-summary">
        <span className="expandable-cell-first">{first.label}</span>
        {first.level && (
          <>
            <span className="expandable-cell-sep">: </span>
            <span className={`expandable-cell-level ${first.level}`}>{first.level}</span>
          </>
        )}
        {tail && <span className="expandable-cell-tail">{tail}</span>}
      </span>
      <span className="expandable-cell-caret">{caret}</span>
    </button>
  );
}
